import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SEGMENT_FIELD_CATALOG,
  type SegmentDefinition,
  type SegmentRule,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from '@/pages/ProjectsPage.module.css';
import { AudienceDynamicRefreshPanel } from '@/features/audiences/AudienceDynamicRefreshPanel';

type Props = {
  audienceId: string;
  projectId: string;
  initialDefinition: SegmentDefinition | null;
  audienceType: string;
  refreshEnabled: boolean;
  refreshRemoveNonMatching: boolean;
  lastRefreshedAt: string | null;
};

function emptyDefinition(): SegmentDefinition {
  return {
    version: 1,
    logic: 'and',
    rules: [],
    exclusions: { respect_suppressions: true, exclude_do_not_contact: true },
  };
}

function newRule(): SegmentRule {
  const first = SEGMENT_FIELD_CATALOG[0]!;
  return {
    id: `r${Date.now()}`,
    entity: first.entity,
    field: first.field,
    op: first.ops[0]!,
    value: first.valueType === 'enum' && first.enumValues?.[0] ? first.enumValues[0] : '',
  };
}

export function AudienceSegmentEditor({
  audienceId,
  projectId,
  initialDefinition,
  audienceType,
  refreshEnabled,
  refreshRemoveNonMatching,
  lastRefreshedAt,
}: Props) {
  const qc = useQueryClient();
  const [definition, setDefinition] = useState<SegmentDefinition>(
    initialDefinition ?? emptyDefinition(),
  );
  const [preview, setPreview] = useState<{
    lead_count: number;
    account_count: number;
  } | null>(null);

  const fieldOptions = useMemo(() => SEGMENT_FIELD_CATALOG, []);

  const saveMut = useMutation({
    mutationFn: () =>
      apiFetch(`/api/audiences/${audienceId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          segment_definition: definition.rules.length ? definition : null,
          audience_type: definition.rules.length ? 'dynamic' : 'static',
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
    },
  });

  const previewMut = useMutation({
    mutationFn: () =>
      apiFetch<{ lead_count: number; account_count: number }>('/api/audiences/preview', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, definition }),
      }),
    onSuccess: (data) => setPreview(data),
  });

  const materializeMut = useMutation({
    mutationFn: () =>
      apiFetch(`/api/audiences/${audienceId}/materialize`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  return (
    <section className={styles.card}>
      <h2>Filters (segment)</h2>
      <p className={styles.mutedSmall}>
        Alleen velden met betrouwbare data. Regels worden in de backend geëvalueerd (AND). Preview
        telt contacten; materialiseren vervangt rule-leden en synchroniseert de campagnelijst.
      </p>

      <ul className={styles.list}>
        {definition.rules.map((rule, idx) => {
          const meta = fieldOptions.find((f) => f.entity === rule.entity && f.field === rule.field);
          return (
            <li key={rule.id} className={styles.formGrid}>
              <Field label="Veld">
                <select
                  className={leadStyles.filterInput}
                  value={`${rule.entity}:${rule.field}`}
                  onChange={(e) => {
                    const [entity, field] = e.target.value.split(':') as [
                      SegmentRule['entity'],
                      string,
                    ];
                    const m = fieldOptions.find((f) => f.entity === entity && f.field === field);
                    if (!m) return;
                    const next = [...definition.rules];
                    next[idx] = {
                      ...rule,
                      entity,
                      field,
                      op: m.ops[0]!,
                      value: m.valueType === 'enum' && m.enumValues?.[0] ? m.enumValues[0] : '',
                    };
                    setDefinition({ ...definition, rules: next });
                  }}
                >
                  {fieldOptions.map((f) => (
                    <option key={`${f.entity}:${f.field}`} value={`${f.entity}:${f.field}`}>
                      {f.label}
                      {f.availability === 'partial' ? ' (deels)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Operator">
                <select
                  className={leadStyles.filterInput}
                  value={rule.op}
                  onChange={(e) => {
                    const next = [...definition.rules];
                    next[idx] = { ...rule, op: e.target.value as SegmentRule['op'] };
                    setDefinition({ ...definition, rules: next });
                  }}
                >
                  {(meta?.ops ?? []).map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </Field>
              {rule.op !== 'exists' && rule.op !== 'not_exists' ? (
                <Field label="Waarde">
                  {meta?.valueType === 'enum' ? (
                    <select
                      className={leadStyles.filterInput}
                      value={String(rule.value ?? '')}
                      onChange={(e) => {
                        const next = [...definition.rules];
                        next[idx] = { ...rule, value: e.target.value };
                        setDefinition({ ...definition, rules: next });
                      }}
                    >
                      {(meta.enumValues ?? []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  ) : meta?.valueType === 'boolean' ? (
                    <select
                      className={leadStyles.filterInput}
                      value={String(rule.value ?? 'true')}
                      onChange={(e) => {
                        const next = [...definition.rules];
                        next[idx] = { ...rule, value: e.target.value === 'true' };
                        setDefinition({ ...definition, rules: next });
                      }}
                    >
                      <option value="true">ja</option>
                      <option value="false">nee</option>
                    </select>
                  ) : (
                    <input
                      className={leadStyles.filterInput}
                      value={String(rule.value ?? '')}
                      onChange={(e) => {
                        const next = [...definition.rules];
                        next[idx] = { ...rule, value: e.target.value };
                        setDefinition({ ...definition, rules: next });
                      }}
                    />
                  )}
                </Field>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setDefinition({
                    ...definition,
                    rules: definition.rules.filter((_, i) => i !== idx),
                  })
                }
              >
                Verwijder regel
              </Button>
            </li>
          );
        })}
      </ul>

      <div className={styles.row}>
        <Button type="button" variant="secondary" onClick={() => setDefinition({ ...definition, rules: [...definition.rules, newRule()] })}>
          Regel toevoegen
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => previewMut.mutate()}
          disabled={!definition.rules.length || previewMut.isPending}
        >
          Preview
        </Button>
        <Button
          type="button"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
        >
          Definitie opslaan
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => materializeMut.mutate()}
          disabled={materializeMut.isPending}
        >
          Materialiseren
        </Button>
      </div>

      {preview ? (
        <p className={styles.mutedSmall}>
          Preview: <strong>{preview.lead_count}</strong> contacten ·{' '}
          <strong>{preview.account_count}</strong> bedrijven
        </p>
      ) : null}

      <AudienceDynamicRefreshPanel
        audienceId={audienceId}
        audienceType={audienceType}
        hasSegment={definition.rules.length > 0}
        refreshEnabled={refreshEnabled}
        refreshRemoveNonMatching={refreshRemoveNonMatching}
        lastRefreshedAt={lastRefreshedAt}
      />
    </section>
  );
}
